import { HeaderInstance } from '../../Header/src/Header';
import { FlyoutHost } from './flyoutIntegrations';
import { HeaderEvents } from './headerEvents.enum';
import { FSSEvent, FlyoutVisibilityEvent } from './headerIntegrations.d';
import { mobileBreakpoints } from '@owc-utils/MediaQueryService/MediaQueryUtils';
import { lockWindowScroll, unlockWindowScroll } from '@owc-utils/common/LockWindow';
import { SecondaryFlyoutInstance } from '../../Header/src/HeaderSecondaryFlyout';

export const seamlessIntegratedPrefixes = [];
// export const seamlessIntegratedPrefixes = [/wishlist-*/];
//
// left commented out as an example for possible future integrations
// const isElementRegistered = (tag: string): boolean => {
//   return document.createElement(tag).constructor !== HTMLElement;
// };
//
// export const initializeWishlist = async (context: HeaderInstance) => {
//   if (context.shouldShowWishlist && !isElementRegistered('wishlist-flyout')) {
//     const { applyPolyfills, defineCustomElements } = await import('@sccs-wishlist/save-button/loader');
//     await applyPolyfills();
//     defineCustomElements();
//   }
// };

let activeFlyout: Element | undefined | null = undefined;
let activeFlyoutName: string | undefined | null = undefined;
let isMaskClick = false;
let isFlyoutClose = false;
let isFlyoutSwap = false; // swap between, or from none to one

export const updateActiveFlyout = (
  newActiveFlyout: Element | null,
  newName: string | null | undefined,
  newStatus: boolean,
  isInnerFlyout = false,
  isBackInteraction = false,
) => {
  isFlyoutClose = Boolean(!newStatus && activeFlyoutName === newName);
  isFlyoutSwap = Boolean(newStatus && activeFlyoutName !== newName);
  isMaskClick = Boolean(!newStatus && !newName);
  if (isFlyoutClose || (isFlyoutSwap && !isInnerFlyout) || isMaskClick) {
    activeFlyout?.dispatchEvent(
      new CustomEvent<{ isBackInteraction: boolean }>(HeaderEvents.FLYOUT_CLOSE_EVENT, {
        detail: { isBackInteraction },
        composed: true,
        bubbles: true,
      }),
    );
  }
  if (isFlyoutClose || isMaskClick) {
    activeFlyout = null;
    activeFlyoutName = null;
  } else if (isFlyoutSwap) {
    activeFlyout = newActiveFlyout;
    activeFlyoutName = newName;
  }

  if (activeFlyout) {
    lockWindowScroll();
  } else {
    unlockWindowScroll();
  }
};

export const handleEvent = (context: HeaderInstance | SecondaryFlyoutInstance, isInnerFlyout = false) => {
  if ((isFlyoutSwap && !isInnerFlyout) || isFlyoutClose || isMaskClick) {
    context.closeHeaderFlyout(true);
  }
  if (isFlyoutSwap) {
    const flyoutLvl = isInnerFlyout ? 3 : 2;
    const chain = [{ infoToAdd: activeFlyoutName, flyoutLevel: flyoutLvl }];

    context.openHeaderFlyout(chain);
  }
  if (isFlyoutSwap || isFlyoutClose) {
    context.setOpenFlyoutId(activeFlyoutName);
  }
};

export const handleFSSFocus = (context: HeaderInstance) => {
  if (context.shouldShowSearchBar) {
    context.$el.addEventListener(
      HeaderEvents.FSS_INPUT_FOCUS_CHANGE,
      ({ target, detail: { isFocused } }: CustomEvent<FSSEvent>) => {
        if (!mobileBreakpoints.includes(context.currentMq)) {
          updateActiveFlyout(target as FlyoutHost, 'fss', isFocused);
          handleEvent(context);
        }
      },
      false,
    );
  }
};

export const handleFlyoutVisibility = (context: HeaderInstance) => {
  context.$el.addEventListener(
    HeaderEvents.FLYOUT_VISIBILITY_CHANGE,
    ({
      target,
      detail: { isOpen, name, isInnerFlyout, isBackInteraction },
    }: CustomEvent<FlyoutVisibilityEvent>) => {
      updateActiveFlyout(target as FlyoutHost, name, isOpen, isInnerFlyout, isBackInteraction);
      handleEvent(context, isInnerFlyout);
    },
    false,
  );
};

/**
 * Event exposed with the single purpose of opening the flyout from an external source.
 * (no button click necessary), used by the Australian market to force the flyout open
 * when the user enters the page, if their location is not specified
 *
 * @param context - Header instance used to see if the user is in mobile mode or not. Since this
 * will only used in mobile mode on page enter, resizes are not a concern.
 */
export const handleMobileLocationPicker = (context: HeaderInstance) => {
  const isMobile = mobileBreakpoints.includes(context.currentMq);
  if (isMobile) {
    context.$el.addEventListener(HeaderEvents.REQUEST_FLYOUT_OPEN, (event: Event) =>
      context.handleHeaderButtonClick(event, 'menu-xs'),
    );
  }
};

export const initializeHeaderIntegrations = (context: HeaderInstance) => {
  handleFSSFocus(context);
  handleFlyoutVisibility(context);
  handleMobileLocationPicker(context);
};

export const handleFlyoutVisibilityOverride = (
  context: SecondaryFlyoutInstance,
  flyout: FlyoutHost,
  options: { isOpen: boolean; name?: string; isInnerFlyout?: boolean },
) => {
  const { isOpen, isInnerFlyout } = options;
  let name = options.name;

  if (!name) {
    name = 'NO_NAME_PROVIDED';
  }

  updateActiveFlyout(flyout, name, isOpen, isInnerFlyout);
  handleEvent(context, isInnerFlyout);
};
